/**
 * SandboxCanvas — the large left canvas of the sandbox detail workspace.
 *
 * Swaps content by instance state, mirroring the Exploratory-Test-Detail
 * canvas (planning → live stream):
 *   - failed             → FailedTakeover
 *   - coming online      → SetupTakeover (the setup checklist coming online)
 *   - online + has agent → SandboxChatView, full-bleed
 *   - online, no agent   → "no agent" empty state (never hits the chat APIs)
 *   - otherwise          → a quiet "not running" empty state
 */
import React from 'react';
import { Button, Card, Text } from '@chatsdet/ui';
import { Moon, Cube, PlugsConnected } from '@phosphor-icons/react';
import type { EnvironmentInstance } from '../../../hooks/useEnvironmentInstances';
import { SandboxChatView } from './SandboxChatView';
import { FailedTakeover } from './FailedTakeover';
import { SetupTakeover } from './SetupTakeover';

export interface SandboxCanvasProps {
  instance: EnvironmentInstance;
  /** instance.status === 'running' — the host is up. */
  runtimeAvailable: boolean;
  /**
   * Whether this sandbox has an interactive agent (`agentType !== 'none'`).
   * Agentless running instances still exist (verification sandboxes, and the
   * launch dialog defaults `withAgent` off), so we must NOT mount the chat
   * surface — and call the sandbox-agent endpoints — for them.
   */
  hasAgent: boolean;
  /** Still bringing the host up (pending → running-but-unhealthy). */
  isSettingUp: boolean;
  isFailed: boolean;
  failureMessage: string | null;
  reloadRef: React.MutableRefObject<(() => void) | null>;
  onRegenerate: () => void;
  onOpenLogs: () => void;
  onConnectLocally: () => void;
}

/** Centered, scrollable wrapper for the takeover/empty states. */
function CenteredCanvas({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full overflow-y-auto bg-muted/10">
      <div className="min-h-full flex items-center justify-center p-6">{children}</div>
    </div>
  );
}

export function SandboxCanvas({
  instance,
  runtimeAvailable,
  hasAgent,
  isSettingUp,
  isFailed,
  failureMessage,
  reloadRef,
  onRegenerate,
  onOpenLogs,
  onConnectLocally,
}: SandboxCanvasProps) {
  if (isFailed) {
    return (
      <CenteredCanvas>
        <FailedTakeover
          message={failureMessage}
          healthCheckResults={instance.healthCheckResults ?? null}
          preservedUntil={instance.preservedForDebugUntil ?? null}
          onRegenerate={onRegenerate}
          onViewLogs={onOpenLogs}
        />
      </CenteredCanvas>
    );
  }

  if (isSettingUp) {
    return (
      <CenteredCanvas>
        <SetupTakeover status={instance.status} />
      </CenteredCanvas>
    );
  }

  if (runtimeAvailable && hasAgent) {
    // Full-bleed chat — the canvas IS the agent surface once the sandbox is up.
    return (
      <div className="h-full min-h-0">
        <SandboxChatView instanceId={instance.id} isEnabled reloadRef={reloadRef} />
      </div>
    );
  }

  if (runtimeAvailable) {
    // Running, but no interactive agent installed — don't touch the chat APIs.
    return (
      <CenteredCanvas>
        <Card padding="lg" className="max-w-[420px] text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <PlugsConnected size={24} className="text-muted-foreground" />
          </div>
          <Text weight="semibold" className="text-base">
            No agent on this sandbox
          </Text>
          <Text size="sm" color="muted" className="mt-1">
            This sandbox is running without an interactive agent. Connect with the CLI to work
            with it, or open the Logs to see what it&apos;s doing.
          </Text>
          <div className="mt-4 flex items-center justify-center gap-2">
            <Button variant="primary" size="sm" onPress={onConnectLocally}>
              Connect locally
            </Button>
            <Button variant="outline" size="sm" onPress={onOpenLogs}>
              View logs
            </Button>
          </div>
        </Card>
      </CenteredCanvas>
    );
  }

  // Stopped / terminated / stopping — no live runtime to chat with.
  const stopping = instance.status === 'stopping';
  return (
    <CenteredCanvas>
      <Card padding="lg" className="max-w-[420px] text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          {stopping ? (
            <Cube size={24} className="text-muted-foreground animate-pulse" />
          ) : (
            <Moon size={24} weight="fill" className="text-muted-foreground" />
          )}
        </div>
        <Text weight="semibold" className="text-base">
          {stopping ? 'Sandbox is stopping…' : 'Sandbox is not running'}
        </Text>
        <Text size="sm" color="muted" className="mt-1">
          {stopping
            ? 'The agent goes offline once the host is released.'
            : 'The agent is offline. Review the run in Overview, Diagnostics, or the Logs viewer.'}
        </Text>
      </Card>
    </CenteredCanvas>
  );
}

SandboxCanvas.displayName = 'SandboxCanvas';
